import React from "react";
import { displayPriceFormat } from "../../../utils";
import { useAppSelector } from "../../../redux/hook";
import {
  DISHOP_GREEN,
  MODIFY_ORDER_TYPE_PROMOTION,
  ORDER_TYPE_DELIVERY,
  PROMOTION_DELIVERY_FREE,
  PROMOTION_PERCENTAGE,

} from "../../../constants";
import OrderPromotion from "./OrderPromotion";
import Spinner from "../../Spinner";
import SkeletonRow from "../../Skeleton/SkeletonRow.view";
import DashedLine from "../../DashedLine/DashedLine";
import { Typography, Box } from "@mui/material";
import getStyles from "../Order.styles";
import { getColors } from '../../../utils'
import { getTitleFromOffer } from '../../ThresholdMarkertingProgress/ThresholdMarketingProgress.service';


type OrderTotalProps = {
  commande?: any;
  promotion?: any;
  showFees: boolean;
  isLoading?: boolean;
  marketingCampaignPromoAmount?: number;
  totalPriceOrder: number;
  isOrderFreeDelivery?: boolean;
  managementFee: number;
  totalProductPriceOrder?: number;
  isPast?: boolean
};

export default function OrderTotal({
  commande,
  promotion,
  showFees,
  isLoading = false,
  marketingCampaignPromoAmount,
  totalPriceOrder,
  isOrderFreeDelivery = false,
  managementFee = 0,
  totalProductPriceOrder,
  isPast
}: OrderTotalProps) {
  const {
    address = {},
    orderType,
    freeDelivery,
  } = useAppSelector((state) => state.pendingOrderReducer);

  const totalProductPriceWithoutFees =
    totalPriceOrder -
    ((freeDelivery ? 0 : (address?.deliveryPrice ?? 0)) + managementFee);

  const { thresholdOffer } = useAppSelector((state) => state.offerReducer);

  const userServicePrice = address?.deliveryPrice ?? 0;

  const styles = getStyles()
  const displayPriceAsync = (price: number) =>
    isLoading ? <Spinner /> : displayPriceFormat(price, false);

  const deliveryIsFree =
    (promotion && promotion.type === PROMOTION_DELIVERY_FREE) ||
    freeDelivery ||
    isOrderFreeDelivery;

  const isClickAndCollectPromo =
    promotion?.reconversionType ===
    MODIFY_ORDER_TYPE_PROMOTION.CLICK_AND_COLLECT;

  const hasPromotion =
    (promotion && !isClickAndCollectPromo) || !!marketingCampaignPromoAmount;
  const isDeliveryOrder = orderType === ORDER_TYPE_DELIVERY;

  const hasFee =
    (showFees && (isDeliveryOrder || managementFee > 0)) ||
    isClickAndCollectPromo;

  const renderPromotion = (promo: any) => {
    if (!promo) return null;

    const isPercentage =
      typeof promo.type === 'string' &&
      promo.type.includes(PROMOTION_PERCENTAGE);

    const promoType =
      isPercentage && !isLoading
        ? promo.type
        : !isLoading && typeof promo.type === 'number'
          ? displayPriceFormat(promo.type)
          : null;

    return (
      promoType && (
        <OrderPromotion
          promotion={promo}
          commande={commande}
          promotionType={promoType}
          prefix='-'
          isLoading={isLoading}
        />
      )
    );
  };

  return (
    <Box sx={{
      ...styles.orderTotalContainer, borderTop: isPast && getColors().border,
      padding: isPast && "16px"
    }}>
      <SkeletonRow
        label='Sous total'
        amount={displayPriceAsync(totalProductPriceOrder)}
        isLoading={isLoading}
        style={{ color: 'unset' }}
      />
      {hasPromotion && (
        <>
          {promotion && !isClickAndCollectPromo && renderPromotion(promotion)}

          {!!marketingCampaignPromoAmount && (
            <SkeletonRow
              label={
                <div style={{ lineHeight: 1.2 }}>
                  {commande
                    ? 'Campagne Marketing'
                    : getTitleFromOffer(thresholdOffer)}
                </div>
              }
              isLoading={isLoading}
              amount={`- ${displayPriceFormat(
                marketingCampaignPromoAmount,
                false,
                true
              )}`}
              style={{ color: DISHOP_GREEN }}
            />
          )}
          <SkeletonRow
            label='Sous total après remise'
            amount={displayPriceAsync(totalProductPriceWithoutFees)}
            isLoading={isLoading}
            style={{ color: 'unset' }}
          />
          <DashedLine />
        </>
      )}

      {hasFee && (
        <>
          {((showFees && isDeliveryOrder) || isClickAndCollectPromo) && (
            <SkeletonRow
              label={<Typography variant="body1">Livraison</Typography>}
              amount={
                <Typography variant="subtitle1">
                  {displayPriceAsync(userServicePrice)}
                </Typography>
              }
              isLoading={isLoading}
              style={{ color: "unset" }}
            />
          )}

          {showFees && managementFee > 0 && (
            <SkeletonRow
              label={<Typography variant="body1">Frais et Services</Typography>}
              amount={
                <Typography variant="subtitle1">
                  {displayPriceAsync(managementFee)}
                </Typography>
              }
              isLoading={isLoading}
            />
          )}

          <DashedLine />
        </>
      )}

      {deliveryIsFree && (
        <>
          <OrderPromotion
            promotion={promotion}
            commande={commande}
            promotionType={displayPriceAsync(userServicePrice)}
            prefix='-'
            isLoading={isLoading}
            freeDelivery={deliveryIsFree}
          />
          <DashedLine />
        </>
      )}

      {promotion && isClickAndCollectPromo && (
        <>
          {" "}
          {renderPromotion(promotion)} <DashedLine />
        </>
      )}

      <SkeletonRow
        label={
          <Typography variant="h5" sx={styles.capitalize}>
            Total TTC
          </Typography>
        }
        amount={
          <Typography variant="subtitle2">
            {displayPriceAsync(totalPriceOrder)}
          </Typography>
        }
        isLoading={isLoading}
      />
    </Box>
  );
}